import Editor from "@draft-js-plugins/editor";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import createMentionPlugin from "@draft-js-plugins/mention";
import { ContentState, EditorState } from "draft-js";
import { I18n } from "@assets/strings";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { createAddLinkPlugin } from "@ui/components/inputs/draft/addLinkPlugin";
import { MentionComponent } from "@ui/components/inputs/draft/useDraftPlugins";

interface DraftRendererProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "content"> {
	content: ContentState;
	fold?: boolean;
	contractibleFold?: boolean;
	backgroundColor?: string;
	foldColor?: string;
	hasInitialMention?: boolean;
}

//rollback LINE_HEIGHT_REM;
const LINE_HEIGHT_REM = 1.25;
const MAX_LINES = 6;

export const DraftRenderer: React.FC<DraftRendererProps> = ({
	content,
	fold = false,
	contractibleFold = false,
	backgroundColor,
	foldColor,
	hasInitialMention,
	...htmlProps
}) => {
	const [richContent, updateContent] = useState(EditorState.createWithContent(content));
	const [isFold, setIsFold] = useState(fold);

	const [maxHeight, setMaxHeight] = useState(0);
	const [contentHeight, setContentHeight] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setMaxHeight(MAX_LINES * LINE_HEIGHT_REM * parseFloat(window.getComputedStyle(document.documentElement).fontSize));
	}, []);

	useEffect(() => {
		setTimeout(() => {
			setContentHeight(containerRef.current?.scrollHeight ?? contentHeight);
		}, 100); // Fixes draft JS Renderer having late dom updates
	}, [content]);

	useEffect(() => {
		if (richContent.getDecorator()) {
			updateContent(EditorState.push(richContent, content, "change-block-data"));
		}
	}, [content]);

	const plugins = useMemo(() => {
		const mentionPlugin = createMentionPlugin({
			mentionComponent: MentionComponent,
		});
		const linkifyPlugin = createLinkifyPlugin({ target: "_blank" });
		const addLinkPlugin = createAddLinkPlugin({ editable: false });

		return [mentionPlugin, linkifyPlugin, addLinkPlugin];
	}, []);

	return (
		<Container {...htmlProps} isFold={isFold} ref={containerRef}>
			<Editor plugins={plugins} readOnly editorState={richContent} onChange={updateContent} />
			{isFold && contentHeight > maxHeight && (
				<Expand
					backgroundColor={backgroundColor}
					color={foldColor}
					onClick={(e) => {
						setIsFold(false);
						e.preventDefault();
					}}
				>
					...{I18n.t("see_more")}
				</Expand>
			)}
			{!isFold && contractibleFold && (
				<Contract
					backgroundColor={backgroundColor}
					color={foldColor}
					onClick={(e) => {
						setIsFold(true);
						e.preventDefault();
					}}
				>
					{I18n.t("see_less")}
				</Contract>
			)}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isFold",
})<{ isFold: boolean }>`
	word-break: break-word;
	position: relative;
	line-height: ${LINE_HEIGHT_REM}rem;
	${({ isFold }) =>
		isFold &&
		css`
			overflow: hidden;
			max-height: ${MAX_LINES * LINE_HEIGHT_REM}rem;
		`}
`;

const Expand = styled.div.withConfig({
	shouldForwardProp: (prop) => !["backgroundColor", "color"].includes(prop),
})<{ backgroundColor?: string; color?: string }>`
	cursor: pointer;
	position: absolute;
	right: 0;
	top: ${(MAX_LINES - 1) * LINE_HEIGHT_REM}rem;
	z-index: 2;
	font-weight: 700;
	padding: 0 4px 0 11px;
	font-size: 0.9375rem;
	${({ color }) => (color ? `color: ${color}` : undefined)};
	background: ${({ theme, backgroundColor }) =>
		`linear-gradient(to right, rgba(255,255,255,0) 0, ${backgroundColor ?? theme.colors.white} 10px)`};
`;

const Contract = styled.div.withConfig({
	shouldForwardProp: (prop) => !["backgroundColor", "color"].includes(prop),
})<{ backgroundColor?: string; color?: string }>`
	cursor: pointer;
	z-index: 2;
	font-weight: 700;
	font-size: 0.9375rem;
	${({ color }) => (color ? `color: ${color}` : undefined)};
	background: ${({ theme, backgroundColor }) =>
		`linear-gradient(to right, rgba(255,255,255,0) 0, ${backgroundColor ?? theme.colors.white} 10px)`};
`;

export const DraftContainer = ".DraftEditor-editorContainer";
